import { useState, type ReactElement } from 'react';

import {
  absoluteUrl,
  displayLink,
  partyPath,
  suggestEntryPath,
  suggestInvitePath,
} from '../routing/paths.js';

import { PartyQrCode } from './PartyQrCode.js';
import { pillButton } from './pillButton.js';
import { RoomModal } from './RoomModal.js';
import { ShareActions } from './ShareActions.js';

import type { PartySlug } from '@mixwire/contracts';

const ROOM_QR_SIZE_PX = 220;

const TABS = [
  { id: 'qr', label: 'QR code' },
  { id: 'suggesters', label: 'Suggesters' },
] as const;

type TabId = (typeof TABS)[number]['id'];

type ShareModalProps = {
  readonly slug: PartySlug;
  readonly partyDisplayName: string;
  readonly passkey: string;
  readonly onClose: () => void;
};

const ACTIVE_TAB = pillButton('solid');
const INACTIVE_TAB = pillButton('quiet');

const TabStrip = ({
  tab,
  onPick,
}: {
  readonly tab: TabId;
  readonly onPick: (next: TabId) => void;
}): ReactElement => (
  <div className="flex flex-wrap justify-center gap-2">
    {TABS.map((candidate) => (
      <button
        key={candidate.id}
        type="button"
        aria-pressed={candidate.id === tab}
        onClick={() => {
          onPick(candidate.id);
        }}
        className={candidate.id === tab ? ACTIVE_TAB : INACTIVE_TAB}
      >
        {candidate.label}
      </button>
    ))}
  </div>
);

const SharePane = ({
  qrPath,
  qrCaption,
  qrAlt,
  link,
  shareUrl,
  shareText,
  children,
}: {
  readonly qrPath: string;
  readonly qrCaption: string;
  readonly qrAlt: string;
  readonly link: string;
  readonly shareUrl: string;
  readonly shareText: string;
  readonly children?: ReactElement;
}): ReactElement => (
  <>
    <PartyQrCode
      path={qrPath}
      size={ROOM_QR_SIZE_PX}
      alwaysVisible
      caption={qrCaption}
      alt={qrAlt}
      className="p-4"
    />
    <p className="font-display text-content text-3xl break-words sm:text-5xl">{link}</p>
    {children}
    <ShareActions url={shareUrl} shareText={shareText} />
  </>
);

/**
 * Everything a DJ hands the room, behind one button: the code to scan, the link
 * to read out, and the way in for someone who wants to suggest a track. Both
 * tabs carry the same kit so no action is ever a tab away.
 */
export const ShareModal = ({
  slug,
  partyDisplayName,
  passkey,
  onClose,
}: ShareModalProps): ReactElement => {
  const [tab, setTab] = useState<TabId>('qr');
  const path = partyPath(slug);
  const link = displayLink(path);
  const invitePath = suggestInvitePath(slug, passkey);
  const entryPath = suggestEntryPath(slug);

  return (
    <RoomModal title={`Share ${partyDisplayName}`} onClose={onClose}>
      <TabStrip tab={tab} onPick={setTab} />

      {tab === 'qr' ? (
        <SharePane
          qrPath={path}
          qrCaption="Scan to follow"
          qrAlt={`QR code linking to ${link}`}
          link={link}
          shareUrl={absoluteUrl(path)}
          shareText={`Follow ${partyDisplayName} on MixWire`}
        />
      ) : (
        <SharePane
          qrPath={invitePath}
          qrCaption="Scan to suggest"
          qrAlt="QR code that opens the suggester view"
          link={displayLink(entryPath)}
          shareUrl={absoluteUrl(invitePath)}
          shareText={`Suggest a track at ${partyDisplayName}`}
        >
          <p className="text-content text-2xl sm:text-3xl">
            and enter <span className="font-mono tracking-widest">{passkey}</span>
          </p>
        </SharePane>
      )}
    </RoomModal>
  );
};
